import { type SuburbType } from '@discover-data/gql/fragments/suburb';

export type ChildAreaBounds = [[number, number], [number, number]];

function hasCoordinates(
  suburb: SuburbType
): suburb is SuburbType & { latitude: number; longitude: number } {
  return (
    typeof suburb.latitude === 'number' &&
    Number.isFinite(suburb.latitude) &&
    typeof suburb.longitude === 'number' &&
    Number.isFinite(suburb.longitude)
  );
}

function zoomIsSet(zoom: SuburbType['zoom']) {
  return typeof zoom === 'number' && zoom > 0;
}

// The area pin is excluded so a pin outside its restaurants cannot stretch the bounds.
export default function getChildAreaBounds(
  area: SuburbType | null,
  suburbs: SuburbType[]
): ChildAreaBounds | null {
  if (!area || zoomIsSet(area.zoom)) {
    return null;
  }

  const children = suburbs.filter(
    (suburb): suburb is SuburbType & { latitude: number; longitude: number } =>
      String(suburb.id) !== String(area.id) &&
      suburb.parentId != null &&
      String(suburb.parentId) === String(area.id) &&
      hasCoordinates(suburb)
  );

  if (children.length < 2) {
    return null;
  }

  let minLatitude = Infinity;
  let maxLatitude = -Infinity;
  let minLongitude = Infinity;
  let maxLongitude = -Infinity;

  for (const child of children) {
    minLatitude = Math.min(minLatitude, child.latitude);
    maxLatitude = Math.max(maxLatitude, child.latitude);
    minLongitude = Math.min(minLongitude, child.longitude);
    maxLongitude = Math.max(maxLongitude, child.longitude);
  }

  return [
    [minLongitude, minLatitude],
    [maxLongitude, maxLatitude],
  ];
}
